import { Link } from 'react-router-dom';
import {
  UXP_AS_ID,
  WORKFORCE_METRICS,
  AGENT_TYPES,
  AGENT_BUILDER_FEATURES,
  WORKFLOW_DESIGNER_FEATURES,
  TRIGGER_TYPES,
  BUSINESS_RULE_FEATURES,
  MULTI_AGENT_FEATURES,
  HUMAN_LOOP_MODES,
  KNOWLEDGE_SOURCES,
  AGENT_MEMORY_FEATURES,
  AUTOMATION_ANALYTICS,
  SECURITY_FEATURES,
  SIMULATION_FEATURES,
  COLLABORATION_FEATURES,
  ACCESSIBILITY_STANDARDS,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_KPIS,
  DEMO_AGENTS,
  DEMO_AGENT_CONFIGS,
  DEMO_WORKFLOWS,
  DEMO_TRIGGERS,
  DEMO_RULES,
  DEMO_MULTI_AGENT,
  DEMO_HUMAN_LOOP,
  buildAutomationStudioDashboard,
} from '../../automationstudio/framework';
import {
  AIWorkforceCommandCenterPanel,
  AIAgentLibraryPanel,
  AgentBuilderPanel,
  WorkflowDesignerPanel,
  TriggerEnginePanel,
  BusinessRuleEnginePanel,
  MultiAgentCollaborationPanel,
  HumanInTheLoopPanel,
} from '../../components/automationstudio';
import { Card, KpiCard } from '../../components/ui';

export function AutomationStudioPage() {
  const dashboard = buildAutomationStudioDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{UXP_AS_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">AI Automation Studio</h1>
        <p className="mt-1 text-sm text-slate-500">
          AI Automation Studio, Agent Builder & Workflow Orchestration Operating System —
          design, deploy, and govern an AI workforce with human oversight.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-medium text-violet-700 hover:bg-violet-100 dark:border-violet-900 dark:bg-violet-950 dark:text-violet-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Workforce Metrics" value={dashboard.workforce_metrics.toString()} />
        <KpiCard label="Agent Types" value={dashboard.agent_types.toString()} />
        <KpiCard label="Builder Features" value={dashboard.builder_features.toString()} />
        <KpiCard label="Workflow Features" value={dashboard.workflow_features.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Architecture Pipeline</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI Workforce Command Center</h2>
        <p className="mt-1 text-sm text-slate-500">{WORKFORCE_METRICS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <AIWorkforceCommandCenterPanel kpis={DEMO_KPIS} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">AI Agent Library</h2>
          <p className="mt-1 text-sm text-slate-500">{AGENT_TYPES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <AIAgentLibraryPanel agents={DEMO_AGENTS} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Agent Builder</h2>
          <p className="mt-1 text-sm text-slate-500">{AGENT_BUILDER_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <AgentBuilderPanel configs={DEMO_AGENT_CONFIGS} />
          </Card>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Workflow Designer</h2>
          <p className="mt-1 text-sm text-slate-500">{WORKFLOW_DESIGNER_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <WorkflowDesignerPanel workflows={DEMO_WORKFLOWS} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Trigger Engine</h2>
          <p className="mt-1 text-sm text-slate-500">{TRIGGER_TYPES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <TriggerEnginePanel triggers={DEMO_TRIGGERS} />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Business Rule Engine</h2>
        <p className="mt-1 text-sm text-slate-500">{BUSINESS_RULE_FEATURES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <BusinessRuleEnginePanel rules={DEMO_RULES} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Multi-Agent Collaboration</h2>
          <p className="mt-1 text-sm text-slate-500">{MULTI_AGENT_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <MultiAgentCollaborationPanel tasks={DEMO_MULTI_AGENT} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Human-in-the-Loop</h2>
          <p className="mt-1 text-sm text-slate-500">{HUMAN_LOOP_MODES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <HumanInTheLoopPanel items={DEMO_HUMAN_LOOP} />
          </Card>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Knowledge Integration</h2>
          <p className="mt-1 text-sm text-slate-500">{KNOWLEDGE_SOURCES.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Agent Memory</h2>
          <p className="mt-1 text-sm text-slate-500">{AGENT_MEMORY_FEATURES.join(' · ')}</p>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">Automation Analytics</h2>
          <p className="mt-2 text-xs text-slate-500">{AUTOMATION_ANALYTICS.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Security</h2>
          <p className="mt-2 text-xs text-slate-500">{SECURITY_FEATURES.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Simulation</h2>
          <p className="mt-2 text-xs text-slate-500">{SIMULATION_FEATURES.join(' · ')}</p>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">Collaboration</h2>
          <p className="mt-2 text-xs text-slate-500">{COLLABORATION_FEATURES.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Accessibility</h2>
          <div className="mt-2 flex flex-wrap gap-1">
            {ACCESSIBILITY_STANDARDS.map((s) => (
              <span key={s} className="rounded border border-slate-200 px-2 py-0.5 text-xs dark:border-slate-700">{s}</span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
